import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "../../lib/api";
import { ListHeader, RowActions, useCrudList } from "./AdminMagazines";
import { Field, inputCls } from "../../components/admin/fields";
import { toast } from "sonner";

export function AdminPlans() {
  const { items, remove } = useCrudList("plans");
  const [mags, setMags] = useState({});
  useEffect(() => { adminApi.get("/magazines").then((r) => setMags(Object.fromEntries(r.items.map((m) => [m.id, m.title])))); }, []);
  return (
    <div data-testid="admin-plans-page">
      <ListHeader title="Subscription Plans" newTo="/apadmin/plans/new" testId="new-plan-btn" />
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="plans-table">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-200">
              <th className="px-4 py-3">Plan</th><th className="px-4 py-3">Magazine(s)</th><th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Price</th><th className="px-4 py-3">MRP</th><th className="px-4 py-3">Active</th><th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 last:border-0" data-testid={`plan-row-${p.id}`}>
                <td className="px-4 py-3 font-medium text-zinc-900">{p.name}</td>
                <td className="px-4 py-3 text-zinc-600">{(p.magazine_ids || []).map((m) => mags[m] || m).join(", ")}</td>
                <td className="px-4 py-3 text-zinc-600">{p.duration_value} {p.duration_unit}</td>
                <td className="px-4 py-3 font-semibold">₹{(p.price || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-zinc-500">{p.original_price ? `₹${p.original_price.toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-4 py-3">{p.active ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span> : <span className="text-xs text-zinc-400">Off</span>}</td>
                <td className="px-4 py-3"><RowActions editTo={`/apadmin/plans/${p.id}/edit`} onDelete={() => remove(p.id)} testId={`plan-${p.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY = { name: "", magazine_ids: [], duration_value: 12, duration_unit: "months", price: 0, original_price: 0, active: true, gateway_plan_id: "" };

export function PlanForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [mags, setMags] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    adminApi.get("/magazines").then((r) => setMags(r.items));
    if (id) adminApi.get(`/plans/${id}`).then(setForm).catch(() => {});
  }, [id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, original_price: parseFloat(form.original_price) || 0, duration_value: parseInt(form.duration_value) || 1 };
      if (id) await adminApi.put(`/plans/${id}`, payload);
      else await adminApi.post("/plans", payload);
      toast.success("Plan saved.");
      navigate("/apadmin/plans");
    } catch (err) {
      toast.error(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl" data-testid="plan-form">
      <h1 className="text-xl font-bold text-zinc-900 mb-6">{id ? "Edit Plan" : "New Plan"}</h1>
      <div className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Field label="Plan name *"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="plan-name-input" /></Field></div>
        <div className="sm:col-span-2">
          <Field label="Magazine(s) covered">
            <div className="border border-zinc-300 rounded-md p-3 max-h-44 overflow-y-auto space-y-2" data-testid="plan-magazines-checklist">
              {mags.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm text-zinc-700">
                  <input type="checkbox" className="accent-zinc-900" checked={(form.magazine_ids || []).includes(m.id)}
                    onChange={(e) => set("magazine_ids", e.target.checked ? [...(form.magazine_ids || []), m.id] : form.magazine_ids.filter((x) => x !== m.id))}
                    data-testid={`plan-mag-${m.slug}`} />
                  {m.title}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <Field label="Duration value"><input type="number" min="1" value={form.duration_value} onChange={(e) => set("duration_value", e.target.value)} className={inputCls} data-testid="plan-duration-input" /></Field>
        <Field label="Duration unit">
          <select value={form.duration_unit} onChange={(e) => set("duration_unit", e.target.value)} className={inputCls} data-testid="plan-unit-select">
            <option value="months">Months</option><option value="issues">Issues</option>
          </select>
        </Field>
        <Field label="Price (INR)"><input type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="plan-price-input" /></Field>
        <Field label="Original price (strike-through)"><input type="number" min="0" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} className={inputCls} data-testid="plan-mrp-input" /></Field>
        <Field label="Gateway plan / link ID (optional)"><input value={form.gateway_plan_id} onChange={(e) => set("gateway_plan_id", e.target.value)} className={inputCls} data-testid="plan-gateway-input" /></Field>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-zinc-900" data-testid="plan-active-check" /> Active (visible on site)
          </label>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <button type="submit" disabled={saving} data-testid="plan-save-btn" className="bg-zinc-900 text-white text-sm font-semibold px-6 py-2.5 rounded-md hover:bg-zinc-700 disabled:opacity-60">
          {saving ? "Saving..." : "Save Plan"}
        </button>
        <button type="button" onClick={() => navigate("/apadmin/plans")} className="text-sm font-semibold px-6 py-2.5 rounded-md border border-zinc-300 hover:border-zinc-900">Cancel</button>
      </div>
    </form>
  );
}
